import { useState, type FC } from "react";
import TextButton from "../../atoms/buttons/TextButton";
import { UserIcon } from "../../atoms/icons/UserIcon";
import styled from "styled-components";
import { Col, Row } from "react-grid-system";
import Subtitle from "../../atoms/typography/Subtitle";
import { theme } from "../../../theme";
import IconButton from "../../atoms/buttons/IconButton";
import { CrossIcon } from "../../atoms/icons/CrossIcon";
import { AnimatePresence, motion } from "framer-motion";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Subheading from "../../atoms/typography/Subheading";
import Body from "../../atoms/typography/Body";
import BodySmall from "../../atoms/typography/BodySmall";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { OpenLinkIconSmall } from "../../atoms/icons/OpenLinkIcon";
import { FixIconSmall } from "../../atoms/icons/FixIcon";
import { SendIcon } from "../../atoms/icons/SendIcon";
import DonutStatSection from "../../molecules/assessment/DonutStatSection";
import OpenInvoicesList from "../../molecules/assessment/OpenInvoicesList";
import RecievedInvoicesList from "../../molecules/assessment/RecievedInvoicesList";
import { useTranslation } from "react-i18next";
import CreditScoreOverviewSmall from "../../molecules/assessment/CreditScoreOverviewSmall";
import { useClient } from "../../../graphql/hooks/useClient";
import { useClientAssessmentInvoices } from "../../../graphql/hooks/invoices/useClientAssessmentInvoices";
import {
  formatAmountWithCurrency,
  formatPrice,
} from "../../../utils/formatters";
import SendInvoiceMessageToClientButton from "../../molecules/invoices/SendInvoiceMessageToClientButton";
import ClientOrgInfo from "../../molecules/client/ClientOrgInfo";
import ClientAddresses from "../../molecules/client/ClientAddresses";
import { useClientStatistics } from "../../../graphql/hooks/client/useClientStatistics";
import type { InvoiceProcessStatus } from "../../../models/InvoiceStatusModel";
import type { InvoiceSortBy } from "../../../models/InvoiceSortBy";
import type { SortDirection } from "../../../models/SortDirection";
import { getAmlStatusBadge } from "../../molecules/ClientCardHeader";

interface Props {
  clientIdentifier: string;
}

const OverlayContent = styled(motion.div)`
  padding: 24px;
  background-color: white;
  border-radius: 4px;
  max-height: 90vh;
  width: 80vw;
  max-width: 1400px;
  position: fixed;
  top: 0;
  right: 0;
  box-shadow: ${theme.shadow.cardShadow};
  z-index: 100;
  overflow-y: scroll;
`;

const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.1);
  z-index: 100;
`;

const FlexedDiv = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const OutlinedContainer = styled.div`
  border: 1px solid ${theme.colors.grey05};
  border-radius: 12px;
  padding: 16px;
`;

const StatContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  border-radius: 8px;
  background-color: ${theme.colors.grey02};
`;

export const IssuerDetailsOverlay: FC<Props> = ({ clientIdentifier }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [openInvoicesPage, setOpenInvoicesPage] = useState(0);
  const [receivedInvoicesPage, setReceivedInvoicesPage] = useState(0);
  const [openInvoicesStatuses, setOpenInvoicesStatuses] = useState<
    Array<string> | undefined
  >(undefined);
  const [openInvoicesSearch, setOpenInvoicesSearch] = useState("");
  const [openInvoicesSortBy, setOpenInvoicesSortBy] =
    useState<InvoiceSortBy>("CREATED_AT");
  const [openInvoicesSortDirection, setOpenInvoicesSortDirection] =
    useState<SortDirection>("DESC");

  const INVOICES_PER_PAGE = 5;

  const { client } = useClient(clientIdentifier);
  const { clientStatistics } = useClientStatistics(clientIdentifier);
  const overdueByCurrency =
    clientStatistics?.invoicesStatistics?.maturityStructure
      ?.overdueByCurrency ?? [];

  const { invoices: openInvoicesData } = useClientAssessmentInvoices(
    clientIdentifier,
    {
      sortBy: openInvoicesSortBy,
      sortDirection: openInvoicesSortDirection,
      invoiceStatuses:
        openInvoicesStatuses && openInvoicesStatuses.length > 0
          ? (openInvoicesStatuses as InvoiceProcessStatus[])
          : ["VALIDATION", "ASSESSMENT"],
      skip: openInvoicesPage * INVOICES_PER_PAGE,
      take: INVOICES_PER_PAGE,
      searchTerm: openInvoicesSearch || undefined,
    },
  );

  const { invoices: receivedInvoicesData } = useClientAssessmentInvoices(
    clientIdentifier,
    {
      sortBy: "CREATED_AT",
      sortDirection: "DESC",
      skip: receivedInvoicesPage * INVOICES_PER_PAGE,
      take: INVOICES_PER_PAGE,
      clientFilterType: "RECIPIENT",
    },
  );

  const openInvoices = (openInvoicesData?.items ?? []).map((inv) => ({
    id: inv.id,
    status: inv?.state?.processStatus ?? "UNKNOWN",
    invoiceNumber: inv.invoiceNumber,
    recipientName: inv.invoiceRecipient?.name ?? "-",
    invoiceDate: new Date(inv.invoiceDate ?? ""),
    dueDate: new Date(inv.dueDate ?? ""),
    amount: inv.totalAmountWithVat ?? 0,
    currency: inv.currencyCode,
  }));

  const receivedInvoices = (receivedInvoicesData?.items ?? []).map((inv) => ({
    id: inv.id,
    status: inv?.state?.processStatus ?? "UNKNOWN",
    invoiceNumber: inv.invoiceNumber,
    issuerName: inv.invoiceIssuer?.client?.name ?? "-",
    invoiceDate: new Date(inv.invoiceDate ?? ""),
    dueDate: new Date(inv.dueDate ?? ""),
    amount: inv.totalAmountWithVat ?? 0,
    currency: inv.currencyCode,
  }));

  const financialLimit = clientStatistics?.limits?.financialLimit;
  const financialTotal = financialLimit?.limit ?? 0;
  const financialUsed = financialLimit?.used ?? 0;
  const financialRemaining = Math.max(financialTotal - financialUsed, 0);

  const recipientLimit = clientStatistics?.limits?.recipientLimit;
  const recipientTotal = recipientLimit?.limit ?? 0;
  const recipientUsed = recipientLimit?.used ?? 0;
  const recipientRemaining = Math.max(recipientTotal - recipientUsed, 0);

  const repurchaseLimit = clientStatistics?.limits?.repurchaseLimit;
  const repurchaseTotal = repurchaseLimit?.limit ?? 0;
  const repurchaseUsed = repurchaseLimit?.used ?? 0;
  const repurchaseRemaining = Math.max(repurchaseTotal - repurchaseUsed, 0);

  return (
    <>
      <TextButton
        color={theme.colors.primary}
        title={t("invoices.issuer")}
        icon={<UserIcon color={theme.colors.primary} />}
        onClick={() => setOpen(true)}
      />

      <AnimatePresence>
        {open && (
          <>
            <Backdrop
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />

            <OverlayContent
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Row nogutter align="center" justify="between">
                <Subtitle color={theme.colors.primary}>
                  {t("invoices.issuer")}
                </Subtitle>
                <IconButton
                  icon={<CrossIcon />}
                  color={theme.colors.white}
                  onClick={() => setOpen(false)}
                />
              </Row>

              <HorizontalSpacer spacing={1} />
              <Row nogutter justify="between" align="start">
                <div
                  style={{ display: "flex", gap: 4, flexDirection: "column" }}
                >
                  <FlexedDiv>
                    <Subheading>{client?.name ?? clientIdentifier}</Subheading>
                    {getAmlStatusBadge(
                      client?.riskScore?.current?.totalScore,
                      client?.riskScore?.current?.riskScoreClass,
                      client?.riskScore?.currentManualRiskScore !== null,
                    )}
                  </FlexedDiv>
                  <BodySmall>
                    {t("common.organisationNumber")}: {clientIdentifier}
                  </BodySmall>
                  <HorizontalSpacer spacing={0.5} />
                  <ClientAddresses
                    clientIdentifier={clientIdentifier}
                    useTitles={true}
                    useBackgroundColor={false}
                  />
                </div>
                <FlexedDiv style={{ gap: 24 }}>
                  <OutlinedButton
                    title={t("clientCard.clientCard")}
                    medium
                    color={theme.colors.primary}
                    icon={<OpenLinkIconSmall color={theme.colors.primary} />}
                    onClick={() =>
                      window.open(`/client/${clientIdentifier}`, "_blank")
                    }
                  />
                  <OutlinedButton
                    title={t("invoiceAssessment.handleLimits")}
                    medium
                    color={theme.colors.primary}
                    icon={<FixIconSmall color={theme.colors.primary} />}
                    onClick={() =>
                      window.open(`/client/${clientIdentifier}`, "_blank")
                    }
                  />
                  <SendInvoiceMessageToClientButton
                    invoiceIds={[]}
                    clientIdentifier={clientIdentifier}
                    renderTrigger={(onClick) => (
                      <OutlinedButton
                        title={t("invoiceAssessment.contactIssuer")}
                        medium
                        color={theme.colors.primary}
                        icon={<SendIcon color={theme.colors.primary} />}
                        onClick={onClick}
                      />
                    )}
                  />
                </FlexedDiv>
              </Row>

              <HorizontalSpacer spacing={1} />

              <Row>
                <Col lg={6}>
                  <OutlinedContainer>
                    <CreditScoreOverviewSmall
                      clientIdentifier={clientIdentifier}
                    />
                  </OutlinedContainer>
                </Col>
                <Col lg={6}>
                  <ClientOrgInfo clientIdentifier={clientIdentifier} />
                </Col>
              </Row>

              <HorizontalSpacer spacing={2} />
              <Row>
                <Col
                  lg={2.5}
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  <StatContainer>
                    <BodySmall>
                      {t("invoiceAssessment.externalLimit")}
                    </BodySmall>
                    <Body fontweight="600">
                      {formatPrice(
                        clientStatistics?.creditData?.creditRating
                          ?.creditLimit ?? 0,
                        0,
                      )}
                    </Body>
                  </StatContainer>
                  <StatContainer>
                    <BodySmall>
                      {t("invoiceAssessment.kfmDebtAmount")}
                    </BodySmall>
                    <Body fontweight="600">
                      {formatPrice(
                        clientStatistics?.creditData?.negativeInformation
                          ?.debtBalance?.totalBalance ?? 0,
                        0,
                      )}{" "}
                      {clientStatistics?.creditData?.negativeInformation
                        ?.debtBalance?.currency ?? ""}
                    </Body>
                  </StatContainer>
                  <StatContainer>
                    <BodySmall>
                      {t("invoiceAssessment.paymentDefaults")}
                    </BodySmall>
                    <Body fontweight="600">
                      {clientStatistics?.creditData?.negativeInformation
                        ?.recordOfNonPayment?.numberOfApplications ?? 0}
                    </Body>
                  </StatContainer>
                  <StatContainer>
                    <BodySmall>
                      {t("invoiceAssessment.overdueAmount")}
                    </BodySmall>
                    {overdueByCurrency.length > 0 ? (
                      overdueByCurrency.map((currency) => (
                        <Body key={currency.currencyCode} fontweight="600">
                          {`${formatAmountWithCurrency(currency.totalAmountIncludingVatOverdue, currency.currencyCode)} (${t("invoiceAssessment.overdueInvoiceCount", { count: currency.numberOfInvoicesOverdue })})`}
                        </Body>
                      ))
                    ) : (
                      <Body fontweight="600">-</Body>
                    )}
                  </StatContainer>
                  <StatContainer>
                    <BodySmall>
                      {t("invoiceAssessment.outstandingAmount")}
                    </BodySmall>
                    {overdueByCurrency.length > 0 ? (
                      overdueByCurrency.map((currency) => (
                        <Body key={currency.currencyCode} fontweight="600">
                          {formatAmountWithCurrency(
                            currency.totalAmountIncludingVatOutstanding,
                            currency.currencyCode,
                          )}
                        </Body>
                      ))
                    ) : (
                      <Body fontweight="600">-</Body>
                    )}
                  </StatContainer>
                </Col>
                <Col lg={9.5}>
                  <Row>
                    <Col lg={6}>
                      <DonutStatSection
                        data={[
                          {
                            id: "1",
                            titleKey: "Använt utrymme",
                            percentage:
                              financialTotal > 0
                                ? (financialUsed / financialTotal) * 100
                                : 0,
                            valueText: `${formatPrice(financialUsed, 0)} kr`,
                          },
                          {
                            id: "2",
                            titleKey: "Återstående limit",
                            percentage:
                              financialTotal > 0
                                ? (financialRemaining / financialTotal) * 100
                                : 0,
                            valueText: `${formatPrice(financialRemaining, 0)} kr`,
                          },
                        ]}
                        colorType="turqoise"
                        title={`Finansieringslimit - ${formatPrice(financialTotal, 0)} kr`}
                      />
                    </Col>
                    <Col lg={6}>
                      <DonutStatSection
                        data={[
                          {
                            id: "1",
                            titleKey: "Använt utrymme",
                            percentage:
                              recipientTotal > 0
                                ? (recipientUsed / recipientTotal) * 100
                                : 0,
                            valueText: `${formatPrice(recipientUsed, 0)} kr`,
                          },
                          {
                            id: "2",
                            titleKey: "Återstående limit",
                            percentage:
                              recipientTotal > 0
                                ? (recipientRemaining / recipientTotal) * 100
                                : 0,
                            valueText: `${formatPrice(recipientRemaining, 0)} kr`,
                          },
                        ]}
                        colorType="midnight"
                        title={`Mottagarlimit - ${formatPrice(recipientTotal, 0)} kr`}
                        link="clientcard"
                      />
                    </Col>
                  </Row>
                  <HorizontalSpacer spacing={1} />
                  <Row>
                    <Col lg={6}>
                      <DonutStatSection
                        data={[
                          {
                            id: "1",
                            titleKey: "Använt utrymme",
                            percentage:
                              repurchaseTotal > 0
                                ? (repurchaseUsed / repurchaseTotal) * 100
                                : 0,
                            valueText: `${formatPrice(repurchaseUsed, 0)} kr`,
                          },
                          {
                            id: "2",
                            titleKey: "Återstående limit",
                            percentage:
                              repurchaseTotal > 0
                                ? (repurchaseRemaining / repurchaseTotal) * 100
                                : 0,
                            valueText: `${formatPrice(repurchaseRemaining, 0)} kr`,
                          },
                        ]}
                        colorType="ocean"
                        title={`Återköpslimit - ${formatPrice(repurchaseTotal, 0)} kr`}
                        link="invoices"
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>

              <HorizontalSpacer spacing={2} />
              <OutlinedContainer>
                <OpenInvoicesList
                  invoices={openInvoices}
                  clientIdentifier={clientIdentifier}
                  currentPage={openInvoicesPage}
                  totalPages={Math.ceil(
                    (openInvoicesData?.totalCount ?? 0) / INVOICES_PER_PAGE,
                  )}
                  onPageChange={setOpenInvoicesPage}
                  selectedStatuses={openInvoicesStatuses}
                  onStatusChange={(statuses) => {
                    setOpenInvoicesStatuses(statuses);
                    setOpenInvoicesPage(0);
                  }}
                  searchTerm={openInvoicesSearch}
                  onSearchChange={(term) => {
                    setOpenInvoicesSearch(term);
                    setOpenInvoicesPage(0);
                  }}
                  sortBy={openInvoicesSortBy}
                  sortDirection={openInvoicesSortDirection}
                  onSortChange={(col, dir) => {
                    setOpenInvoicesSortBy(col);
                    setOpenInvoicesSortDirection(dir);
                    setOpenInvoicesPage(0);
                  }}
                />
              </OutlinedContainer>

              <HorizontalSpacer spacing={2} />
              <OutlinedContainer>
                <RecievedInvoicesList
                  invoices={receivedInvoices}
                  currentPage={receivedInvoicesPage}
                  totalPages={Math.ceil(
                    (receivedInvoicesData?.totalCount ?? 0) / INVOICES_PER_PAGE,
                  )}
                  onPageChange={setReceivedInvoicesPage}
                />
              </OutlinedContainer>
            </OverlayContent>
          </>
        )}
      </AnimatePresence>

      {/*
                      <Col lg={6}>
                  <OutlinedContainer>
                    <TitleSmall color={theme.colors.primary}>
                      {t("random.notes")}
                    </TitleSmall>
                    <HorizontalSpacer spacing={1} />
                    {exampleNotes.map((note, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          gap: 8,
                          justifyContent: "space-between",
                          marginBottom: 16,
                        }}
                      >
                        <div>
                          <Small>
                            {dayjs(note.date).format("YYYY-MM-DD HH:mm")},{" "}
                            {note.author}
                          </Small>
                          <FlexedDiv>
                            {note.critical && (
                              <WarningIcon color={theme.colors.error} />
                            )}
                            <BodySmall>{note.note}</BodySmall>
                          </FlexedDiv>
                        </div>
                        <FlexedDiv style={{ gap: 16 }}>
                          {note.isPinned ? (
                            <PinFilledIcon color={theme.colors.primary} />
                          ) : (
                            <PinIcon color={theme.colors.primary} />
                          )}
                          <DeleteModal
                            deleteButtonType="circleButton"
                            onClickDelete={() => console.log("delete")}
                          />
                        </FlexedDiv>
                      </div>
                    ))}
                    <Divider />
                    <HorizontalSpacer spacing={1} />
                    <MultilineTextField
                      label={t("random.notes")}
                      onChange={(v) => console.log(v)}
                    />
                    <HorizontalSpacer spacing={1} />
                    <Row nogutter justify="between" align="center">
                      <Checkbox
                        checked
                        onChange={() => {}}
                        title={t("invoiceAssessment.pin")}
                      />
                      <FlexedDiv>
                        <DefaultButton title={t("common.cancel")} />
                        <ActionButton
                          title={t("common.save")}
                          color={theme.colors.primary}
                        />
                      </FlexedDiv>
                    </Row>
                  </OutlinedContainer>
                </Col>
      */}
    </>
  );
};
export default IssuerDetailsOverlay;
